import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Button as AntButton,
  Card,
  Dropdown,
  Modal,
  Typography,
  Space,
  Flex,
  Progress,
  Row,
  Col,
  Grid,
  Input,
  Segmented,
  Select,
} from 'antd';
import {
  AlertOutlined,
  AppstoreOutlined,
  BarsOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  DownOutlined,
  FullscreenOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { BlockingReason } from '@evimo/types';

import { useAuth } from '~/app/auth/context/AuthContext';
import { IncidentStationAssetCell } from '@/app/components/incident/IncidentStationAssetCell/IncidentStationAssetCell';
import {
  IncidentService,
  mapIncidentListItemToDashboard,
  type TriggeredIncidentListItem,
} from '~/app/services/incident/incident.service';
import {
  INCIDENT_BLOCKING_REASONS,
  useIncidentStatusTransition,
} from '@/app/hooks/incident/useIncidentStatusTransition';
import { useTriggeredIncidents } from '@/app/hooks/incident/useTriggeredIncidents';
import {
  type DemoIncident,
  type IncidentSeverity,
  type IncidentStatus,
} from '~/app/dashboard/mock-data';
import { IncidentLineChart } from '~/app/dashboard/components/IncidentLineChart';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';
import { incidentDisplayCode } from '~/app/utils/incident-display-code';
import {
  chartFilters,
  dashboardTimeFilters,
  groupedStatusDefinitions,
  incidentStatusConfig,
  severityConfig,
  type ChartFilterKey,
  type TimeFilterKey,
} from '~/app/dashboard/components/SupervisorDashboard.constants';
import { SupervisorOperationsMapCard } from '~/app/dashboard/components/SupervisorOperationsMapCard';
import {
  renderPill,
  resolveDashboardStatusCount,
} from '~/app/dashboard/components/supervisorDashboard.utils';
import { useTimeAgo } from '~/app/hooks/useTimeAgo';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

function mapTriggeredIncidentToDashboard(
  incident: TriggeredIncidentListItem,
): DemoIncident {
  return mapIncidentListItemToDashboard({
    id: incident.id,
    identifier: incident.identifier,
    code: incident.code,
    title: incident.title,
    status: incident.status,
    availableTransitions: incident.availableTransitions ?? [],
    resolutionType: null,
    blockingReason: null,
    severity: incident.severity,
    severityLevel: incident.severityLevel,
    priority: null,
    description: incident.aiSummary,
    aiSummary: incident.aiSummary,
    origin: ['AI diagnosis'],
    stationId: incident.stationId,
    stationName: incident.stationName,
    location: incident.location,
    affectedChargers: incident.affectedChargers,
    totalChargers: incident.totalChargers,
    createdAt: incident.createdAt,
    updatedAt: incident.openedAt ?? incident.createdAt,
    openedAt: incident.openedAt,
    resolvedAt: null,
    closedAt: null,
    primaryAsset: incident.primaryAsset,
  });
}

export function SupervisorDashboard() {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const { t } = useTranslation('incidents');
  const { timeAgo } = useTimeAgo();
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';
  const triggeredIncidents = useTriggeredIncidents();
  const [countTimeFilter, setCountTimeFilter] = useState<TimeFilterKey>('30d');
  const [kpiTimeFilter, setKpiTimeFilter] = useState<TimeFilterKey>('30d');
  const [chartTimeFilter, setChartTimeFilter] = useState<ChartFilterKey>('1m');
  const [incidentView, setIncidentView] = useState<'table' | 'cards'>('table');
  const [chartZoomOpen, setChartZoomOpen] = useState(false);
  const [animProgress, setAnimProgress] = useState(0);
  const animRafRef = useRef<number | null>(null);
  const animStartRef = useRef<number | null>(null);

  const dashboardStatsQuery = useQuery({
    queryKey: [
      'incidents',
      'dashboard-stats',
      organizationId,
      countTimeFilter,
      kpiTimeFilter,
      chartTimeFilter,
    ],
    queryFn: () =>
      IncidentService.getDashboardStats({
        timeRange: countTimeFilter,
        kpiTimeRange: kpiTimeFilter,
        chartRange: chartTimeFilter,
      }),
    enabled: !!organizationId,
  });
  const {
    blockingReason,
    cancelPendingTransition,
    confirmBlockedTransition,
    isTransitionDialogOpen,
    isTransitioning,
    pendingTransition,
    requestTransition,
    setBlockingReason,
    transitioningIncidentId,
  } = useIncidentStatusTransition();

  useEffect(() => {
    const duration = 1400;
    animStartRef.current = null;
    const tick = (timestamp: number) => {
      if (animStartRef.current === null) animStartRef.current = timestamp;
      const t = Math.min((timestamp - animStartRef.current) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setAnimProgress(eased);
      if (t < 1) animRafRef.current = requestAnimationFrame(tick);
    };
    animRafRef.current = requestAnimationFrame(tick);
    return () => {
      if (animRafRef.current !== null) cancelAnimationFrame(animRafRef.current);
    };
  }, []);

  const triggeredTableIncidents: DemoIncident[] =
    triggeredIncidents.incidents.map(mapTriggeredIncidentToDashboard);

  const apiDashboardStats = dashboardStatsQuery.data;
  const chartData = {
    counts: apiDashboardStats?.evolution.counts ?? [],
    labels: apiDashboardStats?.evolution.labels ?? [],
  };

  const groupedStatusCounts = groupedStatusDefinitions.map((group) => {
    const items = group.statuses.map((status: IncidentStatus) => ({
      status,
      label: t(`status.${status}`, status),
      color: incidentStatusConfig[status].color,
      count: resolveDashboardStatusCount(
        apiDashboardStats?.byStatus,
        status,
        0,
      ),
    }));

    return {
      key: group.key,
      label: group.label,
      color: group.color,
      total: items.reduce((sum, item) => sum + item.count, 0),
      items,
    };
  });

  const totalIncidentCount = apiDashboardStats?.total ?? 0;
  const openIncidentCount =
    apiDashboardStats?.open ??
    groupedStatusCounts.find((group) => group.key === 'open')?.total ??
    0;
  const kpiResolvedCount = apiDashboardStats?.kpis.resolvedCount ?? 0;
  const fixTimeFixRate = apiDashboardStats?.kpis.firstTimeFixRate ?? 0;
  const remoteResolutionRate =
    apiDashboardStats?.kpis.remoteResolutionRate ?? 0;
  const successfulStartRate =
    apiDashboardStats?.kpis.successfulStartRate ?? null;
  const meanTimeToRepairHours =
    apiDashboardStats?.kpis.meanTimeToRepairHours ?? null;
  const repairTimeSampleSize =
    apiDashboardStats?.kpis.repairTimeSampleSize ?? 0;
  const remoteResolutionCount = Math.round(
    (remoteResolutionRate / 100) * kpiResolvedCount,
  );

  // Animated display values
  const animTotal = Math.round(animProgress * totalIncidentCount);
  const animOpenSharePct = Math.round(
    animProgress *
      Math.round((openIncidentCount / Math.max(totalIncidentCount, 1)) * 100),
  );
  const animFixRate = Math.round(animProgress * fixTimeFixRate);
  const animSuccessRate =
    successfulStartRate === null
      ? null
      : Math.round(animProgress * successfulStartRate);
  const animMttr =
    meanTimeToRepairHours === null
      ? null
      : Math.round(animProgress * meanTimeToRepairHours * 10) / 10;
  const animRemoteResolutionRate = Math.round(
    animProgress * remoteResolutionRate,
  );
  const animResolvedCount = Math.round(animProgress * kpiResolvedCount);
  const animRemoteCount = Math.round(animProgress * remoteResolutionCount);

  const timeFilterMenuItems: MenuProps['items'] = dashboardTimeFilters.map(
    (filterOption) => ({
      key: filterOption.value,
      label: filterOption.label,
    }),
  );

  const buildTimeFilterMenu = (
    onSelect: (filterKey: TimeFilterKey) => void,
  ): MenuProps => ({
    items: timeFilterMenuItems,
    onClick: ({ key }) => onSelect(key as TimeFilterKey),
  });

  const columns: ColumnsType<DemoIncident> = [
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 88,
      render: (sev: IncidentSeverity) => {
        const cfg = severityConfig[sev];
        return renderPill(cfg.label, cfg.color);
      },
    },
    {
      title: (
        <Space size={4}>
          <span>Incident</span>
          <AntButton
            type="text"
            size="small"
            aria-label={
              triggeredIncidents.sortOrder === 'desc'
                ? t('dashboard.sortOldestFirst', 'Sort oldest first')
                : t('dashboard.sortNewestFirst', 'Sort newest first')
            }
            icon={
              triggeredIncidents.sortOrder === 'desc' ? (
                <CaretDownOutlined />
              ) : (
                <CaretUpOutlined />
              )
            }
            onClick={(event) => {
              event.stopPropagation();
              triggeredIncidents.toggleSortOrder();
            }}
          />
        </Space>
      ),
      dataIndex: 'code',
      key: 'code',
      width: 150,
      render: (_code: string, record) => (
        <Space direction="vertical" size={0} style={{ minWidth: 0 }}>
          <Text
            strong
            ellipsis={{ tooltip: incidentDisplayCode(record) }}
            style={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              color: '#191c1f',
            }}
          >
            {incidentDisplayCode(record)}
          </Text>
          <Text
            strong
            ellipsis={{ tooltip: record.title || undefined }}
            style={{
              fontSize: '0.8125rem',
              fontWeight: 700,
              color: '#191c1f',
            }}
          >
            {record.title?.trim() ? record.title : '—'}
          </Text>
          <Text
            type="secondary"
            style={{ fontSize: '0.6875rem', color: 'var(--evimo-text-muted)' }}
          >
            {timeAgo(record.openedAt ?? record.createdAt)}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Station',
      key: 'station',
      ellipsis: true,
      responsive: ['sm'],
      onCell: () => ({
        style: { minWidth: 220 },
      }),
      render: (_, record) => (
        <IncidentStationAssetCell
          stationName={record.stationName}
          location={record.location}
          primaryAsset={record.primaryAsset}
        />
      ),
    },
    {
      title: 'AI Summary',
      dataIndex: 'aiSummary',
      key: 'aiSummary',
      responsive: ['lg'],
      onCell: () => ({
        style: { minWidth: 220 },
      }),
      render: (text: string) => (
        <Typography.Paragraph
          ellipsis={{ rows: 3, tooltip: text }}
          style={{
            margin: 0,
            fontSize: 14,
            lineHeight: 1.45,
            maxWidth: '100%',
          }}
        >
          {text}
        </Typography.Paragraph>
      ),
    },
    {
      title: 'Impact',
      key: 'impact',
      width: 72,
      align: 'center',
      responsive: ['md'],
      render: (_, record) => {
        const pct = Math.round(
          ((record.totalChargers - record.affectedChargers) /
            record.totalChargers) *
            100,
        );
        return (
          <Space direction="vertical" size={0} style={{ alignItems: 'center' }}>
            <Progress
              type="circle"
              size={40}
              percent={pct}
              strokeColor={
                pct > 75 ? '#00a87e' : pct > 40 ? '#ec7e00' : '#e23b4a'
              }
              format={() =>
                `${record.totalChargers - record.affectedChargers}/${record.totalChargers}`
              }
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              online
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      align: 'center',
      onCell: () => ({
        style: { minWidth: 160, maxWidth: 200, textAlign: 'center' },
      }),
      render: (status: IncidentStatus) => {
        const cfg = incidentStatusConfig[status] ?? {
          color: '#8d969e',
        };
        return renderPill(t(`status.${status}`, status), cfg.color, {
          wrap: true,
        });
      },
    },
    {
      title: t('overview.table.actions', 'Actions'),
      key: 'actions',
      width: 56,
      minWidth: 56,
      align: 'center',
      render: (_, record) => {
        const availableTransitions = record.availableTransitions ?? [];

        if (!availableTransitions.length) {
          return null;
        }

        return (
          <TableRowActionsMenu
            ariaLabel={t('actions.label', 'Update status')}
            loading={isTransitioning && transitioningIncidentId === record.id}
            items={availableTransitions.map((nextStatus) => ({
              key: `${record.id}-${nextStatus}`,
              label: t('actions.transitionTo', {
                status: t(`status.${nextStatus}`, nextStatus),
                defaultValue: 'Move to {{status}}',
              }),
              onClick: () => requestTransition(record.id, nextStatus),
            }))}
          />
        );
      },
    },
  ];

  return (
    <div className="app-page app-page--dashboard">
      {/* Header */}
      <Flex justify="space-between" align="center">
        <Space direction="vertical" size={4}>
          <Title level={2} className="ds-page-title" style={{ margin: 0 }}>
            {t('dashboard.title', 'Dashboard')}
          </Title>
          <Text type="secondary" className="om-dashboard-header__subtitle">
            {t(
              'dashboard.subtitle',
              'Real-time AI-correlated incidents across all connected stations',
            )}
          </Text>
        </Space>
      </Flex>

      <Row gutter={[20, 20]} align="top" style={{ width: '100%' }}>
        <Col xs={24} xl={12} style={{ display: 'flex' }}>
          <Card
            title={<span className="ds-section-header">Incidents Stats</span>}
            extra={
              <Space>
                <Dropdown
                  trigger={['click']}
                  menu={buildTimeFilterMenu((filterKey) => {
                    setCountTimeFilter(filterKey);
                  })}
                >
                  <AntButton
                    type="text"
                    size="small"
                    aria-label="Filter incident counts"
                    style={{ fontSize: 12, color: '#6b7280' }}
                  >
                    {
                      dashboardTimeFilters.find(
                        (f) => f.value === countTimeFilter,
                      )?.label
                    }
                    <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
                  </AntButton>
                </Dropdown>
              </Space>
            }
            style={{
              width: '100%',
              height: '100%',
              borderRadius: 18,
              border: '1px solid #d9e1ec',
              background: '#ffffff',
              overflow: 'visible',
            }}
            styles={{
              body: {
                padding: '12px 16px',
                overflow: 'visible',
                height: '100%',
              },
            }}
          >
            <Flex vertical gap={12} style={{ height: '100%' }}>
              {/* Active Summary */}
              <div
                style={{
                  borderRadius: 14,
                  border: '1px solid #e6edf5',
                  background:
                    'linear-gradient(180deg, #fbfcfe 0%, #f5f8fc 100%)',
                  padding: '6px 12px',
                }}
              >
                <Flex justify="space-between" align="center" gap={16}>
                  <div>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 12,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                      }}
                    >
                      Active Summary
                    </Text>
                    <Text
                      style={{
                        display: 'block',
                        marginTop: 2,
                        color: '#191c1f',
                        fontSize: 22,
                        lineHeight: 1,
                        fontWeight: 700,
                      }}
                    >
                      {animTotal}
                    </Text>
                    <Text
                      type="secondary"
                      style={{ display: 'block', marginTop: 1 }}
                    >
                      incidents currently tracked
                    </Text>
                  </div>
                  <div
                    style={{
                      minWidth: 72,
                      padding: '6px 10px',
                      borderRadius: 12,
                      background: '#ffffff',
                      border: '1px solid #e6edf5',
                    }}
                  >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Open share
                    </Text>
                    <Text
                      style={{
                        display: 'block',
                        marginTop: 2,
                        color: '#191c1f',
                        fontSize: 20,
                        fontWeight: 700,
                        lineHeight: 1.1,
                      }}
                    >
                      {animOpenSharePct}%
                    </Text>
                  </div>
                </Flex>
              </div>

              {/* Open & Resolved side by side */}
              <Row gutter={[8, 8]}>
                {groupedStatusCounts.map((group) => (
                  <Col key={group.key} xs={24} md={12}>
                    <div
                      style={{
                        height: '100%',
                        padding: '6px 10px',
                        borderRadius: 12,
                        border: '1px solid #e8eef5',
                        background: '#ffffff',
                      }}
                    >
                      <Flex align="center" justify="space-between" gap={8}>
                        <Flex align="center" gap={8} style={{ minWidth: 0 }}>
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 999,
                              background: group.color,
                              flexShrink: 0,
                            }}
                          />
                          <Text
                            style={{
                              color: '#191c1f',
                              fontSize: 15,
                              lineHeight: 1.15,
                              whiteSpace: 'nowrap',
                              fontWeight: 600,
                            }}
                          >
                            {group.label}
                          </Text>
                        </Flex>
                        <Text
                          style={{
                            color: '#191c1f',
                            fontSize: 20,
                            lineHeight: 1.15,
                            fontWeight: 700,
                          }}
                        >
                          {Math.round(animProgress * group.total)}
                        </Text>
                      </Flex>
                      <Flex
                        vertical
                        gap={2}
                        style={{
                          marginTop: 4,
                          marginLeft: 4,
                          paddingLeft: 14,
                          borderLeft: '2px solid #edf2f7',
                        }}
                      >
                        {group.items.map((item) => (
                          <Flex
                            key={item.status}
                            align="center"
                            justify="space-between"
                            gap={8}
                          >
                            <Flex
                              align="center"
                              gap={6}
                              style={{ minWidth: 0 }}
                            >
                              <span
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: 999,
                                  background: item.color,
                                  flexShrink: 0,
                                }}
                              />
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {item.label}
                              </Text>
                            </Flex>
                            <Text strong style={{ fontSize: 13 }}>
                              {Math.round(animProgress * item.count)}
                            </Text>
                          </Flex>
                        ))}
                      </Flex>
                    </div>
                  </Col>
                ))}
              </Row>

              {/* Incidents Evolution chart */}
              <div>
                <Flex
                  justify="space-between"
                  align="center"
                  style={{ marginBottom: 8 }}
                >
                  <Text
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#374151',
                    }}
                  >
                    Incidents Evolution
                  </Text>
                  <Space>
                    <div
                      style={{
                        display: 'inline-flex',
                        background: '#f0f4fa',
                        borderRadius: 10,
                        padding: 3,
                        gap: 2,
                      }}
                    >
                      {chartFilters.map((f) => {
                        const active = chartTimeFilter === f.value;
                        return (
                          <button
                            key={f.value}
                            onClick={() => setChartTimeFilter(f.value)}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 1,
                              padding: '4px 10px',
                              borderRadius: 7,
                              border: 'none',
                              cursor: 'pointer',
                              background: active ? '#ffffff' : 'transparent',
                              boxShadow: active
                                ? '0 1px 4px rgba(0,0,0,0.10)'
                                : 'none',
                              transition: 'all 0.18s',
                            }}
                          >
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: active ? 600 : 400,
                                color: active ? '#191c1f' : '#6b7280',
                                lineHeight: 1.3,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {f.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <AntButton
                      type="text"
                      size="small"
                      icon={<FullscreenOutlined />}
                      onClick={() => setChartZoomOpen(true)}
                      aria-label="Zoom chart"
                    />
                  </Space>
                </Flex>
                <IncidentLineChart
                  counts={chartData.counts}
                  labels={chartData.labels}
                />
              </div>
            </Flex>
          </Card>
        </Col>

        <Col xs={24} xl={12} style={{ display: 'flex' }}>
          <Card
            title={<span className="ds-section-header">Operational KPIs</span>}
            extra={
              <Dropdown
                trigger={['click']}
                menu={buildTimeFilterMenu((filterKey) => {
                  setKpiTimeFilter(filterKey);
                })}
              >
                <AntButton
                  type="text"
                  size="small"
                  aria-label="Filter KPI card"
                  style={{ fontSize: 12, color: '#6b7280' }}
                >
                  {
                    dashboardTimeFilters.find((f) => f.value === kpiTimeFilter)
                      ?.label
                  }
                  <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
                </AntButton>
              </Dropdown>
            }
            className="supervisor-kpi-card"
            style={{
              width: '100%',
              borderRadius: 18,
              border: '1px solid #d9e1ec',
            }}
            styles={{
              body: {
                padding: 22,
              },
            }}
          >
            <Row gutter={[16, 16]} className="supervisor-kpi-grid">
              {/* Top-left: First-Time Fix Rate */}
              <Col xs={24}>
                <div
                  className="supervisor-kpi-tile"
                  style={{
                    borderRadius: 18,
                    border: '1px solid #d9e4f0',
                    background:
                      'linear-gradient(180deg, #fbfcfe 0%, #f1f5fa 100%)',
                    padding: 16,
                  }}
                >
                  <Text
                    type="secondary"
                    style={{
                      fontSize: 12,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    First-Time Fix Rate
                  </Text>
                  <Flex
                    align="flex-start"
                    wrap="wrap"
                    gap={14}
                    style={{ marginTop: 12 }}
                  >
                    <Progress
                      type="dashboard"
                      percent={animFixRate}
                      format={(percent) => `${percent ?? 0}%`}
                      size={100}
                      strokeColor="#00a87e"
                      trailColor="#dcefe3"
                    />
                    <Text
                      type="secondary"
                      style={{ fontSize: 13, flex: '1 1 160px', minWidth: 0 }}
                    >
                      Incidents resolved on the first attempt — remote,
                      automated, or on-site (first visit)
                    </Text>
                  </Flex>
                </div>
              </Col>

              {/* Top-right: First-Time Charge Success Rate */}
              <Col xs={24}>
                <div
                  className="supervisor-kpi-tile"
                  style={{
                    borderRadius: 18,
                    border: '1px solid #d9e4f0',
                    background:
                      'linear-gradient(180deg, #fafcff 0%, #f0f4fa 100%)',
                    padding: 16,
                  }}
                >
                  <Text
                    type="secondary"
                    style={{
                      fontSize: 12,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    First-Time Charge Success Rate
                  </Text>
                  <Flex
                    align="flex-start"
                    wrap="wrap"
                    gap={14}
                    style={{ marginTop: 12 }}
                  >
                    <Progress
                      type="dashboard"
                      percent={animSuccessRate ?? 0}
                      format={() =>
                        animSuccessRate === null ? '—' : `${animSuccessRate}%`
                      }
                      size={100}
                      strokeColor="#1E6DF6"
                      trailColor="#dbe6ff"
                    />
                    <Text
                      type="secondary"
                      style={{ fontSize: 13, flex: '1 1 160px', minWidth: 0 }}
                    >
                      {animSuccessRate === null
                        ? t(
                            'dashboard.kpi.successfulStartUnavailable',
                            'Requires session telemetry (not available from incident stats yet)',
                          )
                        : 'Sessions started successfully on first plug-in attempt'}
                    </Text>
                  </Flex>
                </div>
              </Col>

              {/* Bottom-left: Remote Resolution Rate */}
              <Col xs={24}>
                <div
                  className="supervisor-kpi-tile"
                  style={{
                    borderRadius: 18,
                    border: '1px solid #d9e4f0',
                    background:
                      'linear-gradient(180deg, #fbfcfe 0%, #f2f5f9 100%)',
                    padding: 16,
                  }}
                >
                  <Text
                    type="secondary"
                    style={{
                      fontSize: 12,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    Remote Resolution Rate
                  </Text>
                  <Flex
                    align="flex-start"
                    wrap="wrap"
                    gap={14}
                    style={{ marginTop: 12 }}
                  >
                    <Progress
                      type="dashboard"
                      percent={animRemoteResolutionRate}
                      format={(percent) => `${percent ?? 0}%`}
                      size={100}
                      strokeColor="#722ed1"
                      trailColor="#e5d5f7"
                    />
                    <Space
                      direction="vertical"
                      size={4}
                      style={{ flex: '1 1 160px', minWidth: 0 }}
                    >
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        Resolved remotely or via self-healing, no on-site visit
                        required
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {animRemoteCount} of {animResolvedCount} resolved
                        incidents
                      </Text>
                    </Space>
                  </Flex>
                </div>
              </Col>

              {/* Bottom-right: Mean Time To Repair */}
              <Col xs={24}>
                <div
                  className="supervisor-kpi-tile"
                  style={{
                    borderRadius: 18,
                    border: '1px solid #d9e4f0',
                    background:
                      'linear-gradient(180deg, #fafbfd 0%, #f0f4f8 100%)',
                    padding: 16,
                    minWidth: 0,
                  }}
                >
                  <Text
                    type="secondary"
                    style={{
                      fontSize: 12,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    {t('dashboard.mttr.title', 'Mean Time To Repair')}
                  </Text>
                  <Text
                    style={{
                      display: 'block',
                      marginTop: 8,
                      color: '#191c1f',
                      fontSize: 36,
                      lineHeight: 1,
                      fontWeight: 700,
                    }}
                  >
                    {animMttr === null
                      ? t('dashboard.mttr.emptyValue', '—')
                      : t('dashboard.mttr.value', '{{hours}}h', {
                          hours: animMttr,
                        })}
                  </Text>
                  <Text
                    type="secondary"
                    style={{ fontSize: 13, display: 'block', marginTop: 4 }}
                  >
                    {repairTimeSampleSize === 0
                      ? t(
                          'dashboard.mttr.noSamples',
                          'No valid repair-time samples in this period',
                        )
                      : t(
                          'dashboard.mttr.subtitle',
                          'Average time from detection to resolution · Based on {{count}} incidents',
                          { count: repairTimeSampleSize },
                        )}
                  </Text>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <SupervisorOperationsMapCard />

      {/* Chart Zoom Modal */}
      <Modal
        open={chartZoomOpen}
        onCancel={() => setChartZoomOpen(false)}
        footer={null}
        width="90vw"
        title={<span className="ds-section-header">Incidents Evolution</span>}
        centered
        styles={{ body: { padding: '8px 20px 24px' } }}
      >
        <Segmented
          size="small"
          value={chartTimeFilter}
          onChange={(val) => setChartTimeFilter(val as ChartFilterKey)}
          options={chartFilters.map((f) => ({
            label: f.label,
            value: f.value,
          }))}
          style={{ marginBottom: 16 }}
        />
        <IncidentLineChart
          counts={chartData.counts}
          labels={chartData.labels}
        />
      </Modal>

      {/* Incidents Table */}
      <Card
        title={
          <Space align="center">
            <AlertOutlined />
            <span className="ds-section-header">Triggered Incidents</span>
            <AntButton
              type="text"
              size="small"
              onClick={() => navigate('/incidents')}
              style={{
                padding: '0 4px',
                fontSize: 12,
                color: '#8d969e',
                fontWeight: 400,
              }}
            >
              See all
            </AntButton>
          </Space>
        }
        extra={
          <Flex gap={12} wrap="wrap" justify="flex-end">
            <Segmented
              value={incidentView}
              onChange={(value) => setIncidentView(value as 'table' | 'cards')}
              options={[
                {
                  label: <BarsOutlined title="Table view" />,
                  value: 'table',
                },
                {
                  label: <AppstoreOutlined title="Card view" />,
                  value: 'cards',
                },
              ]}
            />
            <Input
              allowClear
              prefix={<SearchOutlined />}
              value={triggeredIncidents.searchValue}
              onChange={(event) => {
                triggeredIncidents.handleSearchChange(event.target.value);
              }}
              style={{
                width: screens.sm ? 320 : 220,
                maxWidth: '100%',
                borderRadius: 8,
              }}
            />
          </Flex>
        }
        styles={{ body: { padding: incidentView === 'table' ? 0 : 20 } }}
        style={{
          width: '100%',
          borderRadius: 16,
          border: '1px solid #e2e8f0',
        }}
      >
        {incidentView === 'table' ? (
          <EvimoTable<DemoIncident>
            className="ds-table"
            columns={columns}
            dataSource={triggeredTableIncidents}
            loading={
              triggeredIncidents.isFetching || triggeredIncidents.isLoading
            }
            rowKey="id"
            stickyLastColumn
            scrollBody={false}
            pagination={{
              current: triggeredIncidents.meta.page,
              pageSize: 5,
              total: triggeredIncidents.meta.total,
              showSizeChanger: false,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} incidents`,
              onChange: (page) => triggeredIncidents.handlePageChange(page),
            }}
            size={screens.md ? 'middle' : 'small'}
            style={{ width: '100%' }}
            scroll={screens.xl ? undefined : { x: 1100 }}
            tableLayout="auto"
            onRow={(record) => ({
              onClick: () => navigate(`/incidents/${record.id}`),
              style: {
                cursor: 'pointer',
                ...(record.severity === 'critical'
                  ? { background: 'rgba(226, 59, 74, 0.05)' }
                  : {}),
              },
            })}
            locale={{ emptyText: 'No incidents' }}
          />
        ) : (
          <Row gutter={[16, 16]}>
            {triggeredTableIncidents.map((record) => {
              const status = incidentStatusConfig[record.status] ?? {
                color: '#c9c9cd',
              };
              const severity = severityConfig[record.severity];
              const onlineCount =
                record.totalChargers - record.affectedChargers;
              const pct = Math.round(
                (onlineCount / record.totalChargers) * 100,
              );

              return (
                <Col key={record.id} xs={24} md={12} xl={8}>
                  <Card
                    hoverable
                    onClick={() => navigate(`/incidents/${record.id}`)}
                    style={{
                      height: '100%',
                      borderRadius: 16,
                      border: '1px solid #e2e8f0',
                      ...(record.severity === 'critical'
                        ? { background: 'rgba(226, 59, 74, 0.03)' }
                        : {}),
                    }}
                    styles={{ body: { padding: 18 } }}
                  >
                    <Flex vertical gap={14} style={{ height: '100%' }}>
                      <Flex justify="space-between" align="flex-start" gap={12}>
                        <Space wrap>
                          {renderPill(severity.label, severity.color)}
                          {renderPill(
                            t(`status.${record.status}`, record.status),
                            status.color,
                            { wrap: true },
                          )}
                        </Space>
                        <Text
                          type="secondary"
                          style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                        >
                          {timeAgo(record.createdAt)}
                        </Text>
                      </Flex>

                      <div>
                        <Text
                          strong
                          style={{ display: 'block', fontFamily: 'monospace' }}
                        >
                          {incidentDisplayCode(record)}
                        </Text>
                        <Text strong style={{ display: 'block', marginTop: 4 }}>
                          {record.stationName}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {record.location}
                        </Text>
                      </div>

                      <Typography.Paragraph
                        ellipsis={{ rows: 3, tooltip: record.aiSummary }}
                        style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}
                      >
                        {record.aiSummary}
                      </Typography.Paragraph>

                      <Flex
                        justify="space-between"
                        align="center"
                        style={{ marginTop: 'auto' }}
                      >
                        <Space direction="vertical" size={2}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Charger availability
                          </Text>
                          <Text strong>
                            {onlineCount}/{record.totalChargers} online
                          </Text>
                        </Space>
                        <Progress
                          type="circle"
                          size={52}
                          percent={pct}
                          strokeColor={
                            pct > 75
                              ? '#00a87e'
                              : pct > 40
                                ? '#ec7e00'
                                : '#e23b4a'
                          }
                          format={() => `${pct}%`}
                        />
                      </Flex>
                    </Flex>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Card>

      <Modal
        open={isTransitionDialogOpen}
        title={t('actions.blockedModal.title', 'Set blocking reason')}
        onCancel={cancelPendingTransition}
        footer={[
          <AntButton
            key="cancel"
            onClick={cancelPendingTransition}
            disabled={isTransitioning}
          >
            {t('actions.cancel', 'Cancel')}
          </AntButton>,
          <AntButton
            key="confirm"
            type="primary"
            loading={isTransitioning}
            onClick={() => {
              void confirmBlockedTransition();
            }}
          >
            {t('actions.confirm', 'Confirm')}
          </AntButton>,
        ]}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text>
            {t(
              'actions.blockedModal.description',
              'Choose the reason that blocks this incident before moving it to Blocked.',
            )}
          </Text>
          <Select<BlockingReason>
            value={blockingReason}
            options={INCIDENT_BLOCKING_REASONS.map((reason) => ({
              label: t(`blockingReason.${reason}`, reason),
              value: reason,
            }))}
            onChange={setBlockingReason}
          />
          {pendingTransition ? (
            <Text type="secondary">
              {t('actions.blockedModal.targetStatus', {
                status: t(
                  `status.${pendingTransition.nextStatus}`,
                  pendingTransition.nextStatus,
                ),
                defaultValue: 'Next status: {{status}}',
              })}
            </Text>
          ) : null}
        </Space>
      </Modal>
    </div>
  );
}
